import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toPng } from 'html-to-image';
import { Baby, Cake, TreePine, Sparkles, Loader2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Occasion = 'new_baby' | 'birthday' | 'holiday' | 'general';
type Tier = 'keeper' | 'storyteller';

interface GiftCardPreviewProps {
  recipientName: string;
  gifterName: string;
  message: string;
  occasion: Occasion | null;
  tier: Tier | null;
  showDownload?: boolean;
}

const OCCASION_ICONS: Record<Occasion, LucideIcon> = {
  new_baby: Baby,
  birthday: Cake,
  holiday:  TreePine,
  general:  Sparkles,
};

const TIER_LABELS: Record<Tier, string> = {
  keeper:      'Mori Plus',
  storyteller: 'Mori Premium',
};

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

export function GiftCardPreview({
  recipientName,
  gifterName,
  message,
  occasion,
  tier,
  showDownload = false,
}: GiftCardPreviewProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const Icon = occasion ? OCCASION_ICONS[occasion] : Sparkles;
  const tierLabel = tier ? TIER_LABELS[tier] : 'Mori Keeper';

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 3,
      });
      const link = document.createElement('a');
      link.download = `mori-gift-for-${(recipientName || 'recipient').replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to export gift card', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      {/* This div is the exact element captured by html-to-image */}
      <div
        ref={cardRef}
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          padding: '32px',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FAF8F5 0%, #F1ECE1 100%)',
          border: '1px solid rgba(255,255,255,0.6)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
          fontFamily: FONT,
        }}
      >
        {/* Watermark — SVG icon, not emoji, so it renders consistently on all OS */}
        <div
          style={{
            position: 'absolute',
            top: -16,
            right: -16,
            opacity: 0.06,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <Icon size={160} strokeWidth={1} />
        </div>

        {/* Header */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p style={{
            fontFamily: FONT,
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#78716c',
            marginBottom: '6px',
          }}>
            {t("giftCard.aGiftOfMemories")}
          </p>
          <h2 style={{
            fontFamily: FONT,
            fontSize: '28px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: '#1c1917',
            margin: 0,
          }}>
            For {recipientName || '…'}
          </h2>
        </div>

        {/* Message */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          padding: '16px 0',
        }}>
          <p style={{
            fontFamily: FONT,
            fontSize: '15px',
            fontWeight: 300,
            lineHeight: 1.6,
            fontStyle: 'italic',
            color: '#44403c',
            margin: 0,
          }}>
            "{message || t("giftCard.placeholderMessage")}"
          </p>
        </div>

        {/* Footer */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid rgba(41,37,36,0.1)',
        }}>
          <div>
            <p style={{ fontFamily: FONT, fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#78716c', margin: 0 }}>{t("giftCard.oneYearOf")}</p>
            <p style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#1c1917', margin: '2px 0 0' }}>{tierLabel}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontFamily: FONT, fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#78716c', margin: 0 }}>{t("giftCard.from")}</p>
            <p style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#1c1917', margin: '2px 0 0' }}>{gifterName || '…'}</p>
          </div>
        </div>
      </div>

      {showDownload && (
        <Button
          onClick={handleDownload}
          disabled={isExporting || !recipientName}
          variant="outline"
          className="rounded-full px-6"
        >
          {isExporting
            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing…</>
            : 'Download to print'
          }
        </Button>
      )}
    </div>
  );
}
